import { useEffect, useState } from "react";
import { useTest } from "../context/TestContext";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase/firebase";
import Timer from "../components/Timer";
import Question from "../components/Question";
import { fetchQuestionsByTestId } from "../services/questionService";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

function TestPage({ user, onExit }) {
  const {
    selectedTest,
    questions,
    setQuestions,
    currentIndex,
    setCurrentIndex,
    isPaused,
    setIsPaused,
    resetTest,
    setTimeLeft,
    answers
  } = useTest();

  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [showFinishWarning, setShowFinishWarning] = useState(false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [testSaved, setTestSaved] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [completionTime, setCompletionTime] = useState(null);

  useEffect(() => {
    if (!selectedTest) return;

    const loadQuestions = async () => {
      // console.log("=== Loading Questions ===");
      // console.log("Test ID:", selectedTest.id);
      const fetchedQuestions = await fetchQuestionsByTestId(selectedTest.id);
      // console.log("Fetched questions:", fetchedQuestions);

      setQuestions(fetchedQuestions);

      if (selectedTest.isTimed && selectedTest.timeLimit) {
        setTimeLeft(selectedTest.timeLimit * 60);
      }

      setStartTime(new Date());
    };

    loadQuestions();
  }, [selectedTest, setQuestions, setTimeLeft]);

  const handleLogout = async () => {
    setShowLogoutWarning(false);
    await signOut(auth);
    setShowDropdown(false);
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
  };

  const handleExit = () => {
    if (testSaved || reviewMode) {
      resetTest();
      onExit();
    } else {
      setShowExitWarning(true);
    }
  };

  const confirmExit = () => {
    setShowExitWarning(false);
    resetTest();
    onExit();
  };

  const normalizeCorrectAnswers = (correctAnswers) => {
    if (Array.isArray(correctAnswers)) {
      return correctAnswers.map(s => String(s).trim());
    } else if (typeof correctAnswers === 'string') {
      return correctAnswers.split(',').map(s => s.trim());
    }
    return [];
  };

  const calculateScore = () => {
    let correctCount = 0;

    // console.log("=== calculateScore START ===");
    // console.log("Total questions:", questions.length);
    // console.log("Total answers:", Object.keys(answers).length);
    // console.log("Answers object:", answers);
    // console.log("Questions:", questions);

    questions.forEach((q, index) => {
      const answer = answers[index];

      // console.log(`\n--- Processing Question ${index + 1} ---`);
      // console.log("Question data:", q);
      // console.log("User answer:", answer);

      if (!answer) {
        // console.log("No answer provided for this question, skipping");
        return;
      }

      const questionType = q.question_type || "single_choice";
      // console.log("Question type:", questionType);

      if (questionType === "single_choice") {
        const normalizedAnswer = String(answer).trim();
        const correctAnswer = q.correct_answer !== undefined ? q.correct_answer : q.correct_answers;
        const normalizedCorrect = String(correctAnswer).trim();

        // console.log(`User answered "${normalizedAnswer}"`);
        // console.log(`q.correct_answer: ${q.correct_answer}`);
        // console.log(`q.correct_answers: ${q.correct_answers}`);
        // console.log(`Using correct answer: "${correctAnswer}"`);
        // console.log(`Normalized user answer: "${normalizedAnswer}"`);
        // console.log(`Normalized correct answer: "${normalizedCorrect}"`);
        // console.log(`Match: ${normalizedAnswer === normalizedCorrect}`);

        if (normalizedAnswer === normalizedCorrect) {
          correctCount++;
          // console.log("✓ Answer marked as correct!");
        } else {
          // console.log("✗ Answer marked as incorrect");
        }
      } else if (questionType === "multi_select") {
        const normalizedCorrect = normalizeCorrectAnswers(q.correct_answers);
        const normalizedAnswer = Array.isArray(answer) ? answer.map(a => String(a).trim()) : [String(answer).trim()];
        const isCorrect = normalizedAnswer.length === normalizedCorrect.length &&
          normalizedAnswer.every(a => normalizedCorrect.includes(a));

        // console.log(`User answered [${normalizedAnswer.join(", ")}]`);
        // console.log(`Correct answers [${normalizedCorrect.join(", ")}]`);
        // console.log(`Match: ${isCorrect}`);

        if (isCorrect) {
          correctCount++;
          // console.log("✓ Answer marked as correct!");
        } else {
          // console.log("✗ Answer marked as incorrect");
        }
      }
    });

    // console.log("\n=== calculateScore END ===");
    // console.log("Final score:", correctCount, "out of", questions.length);

    return correctCount;
  };

  const getScoreDescription = (percentage) => {
    if (percentage < 60) {
      return {
        level: "Needs Improvement",
        status: "Stop. Significant knowledge gaps in core concepts.",
        graphic: "🔴",
        color: "#dc3545"
      };
    } else if (percentage >= 60 && percentage < 70) {
      return {
        level: "Below Target",
        status: "Proceed with Caution. You understand the basics but lack consistency.",
        graphic: "⚠️",
        color: "#ffc107"
      };
    } else if (percentage >= 70 && percentage <= 80) {
      return {
        level: "Target",
        status: "Ready. This is \"safe zone\" for most simulators.",
        graphic: "✅",
        color: "#28a745"
      };
    } else {
      return {
        level: "Above Target",
        status: "🌟 Mastery. Highly likely to pass with a comfortable margin.",
        graphic: "🏆",
        color: "#007bff"
      };
    }
  };

  const formatTimeTaken = (seconds) => {
    if (!seconds) return "0 min 0 sec";
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes} min ${secs} sec`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
  };

  const handleFinish = () => {
    const answeredCount = Object.keys(answers).length;
    const remaining = questions.length - answeredCount;

    if (remaining === 0) {
      saveResult();
    } else {
      setShowFinishWarning(true);
    }
  };

  const confirmFinish = () => {
    setShowFinishWarning(false);
    saveResult();
  };

  const handleReviewStart = () => {
    setShowCompletionPopup(false);
    setReviewMode(true);
    setCurrentIndex(0);
  };

  const saveResult = async () => {
    if (testSaved || !user || !selectedTest) return;

    try {
      const score = calculateScore();
      const percentage = Math.round((score / questions.length) * 100);

      const endTime = new Date();
      const timeTakenSeconds = Math.floor((endTime - startTime) / 1000);
      const completionTimestamp = serverTimestamp();

      await addDoc(collection(db, "results"), {
        userId: user.uid,
        testId: selectedTest.id,
        testTitle: selectedTest.title,
        score: score,
        totalQuestions: questions.length,
        percentage: percentage,
        timeTaken: timeTakenSeconds,
        createdAt: completionTimestamp
      });

      const scoreDesc = getScoreDescription(percentage);

      setTestResult({
        testTitle: selectedTest.title,
        score: score,
        totalQuestions: questions.length,
        percentage: percentage,
        timeTaken: timeTakenSeconds,
        createdAt: endTime,
        scoreDescription: scoreDesc
      });

      setCompletionTime(endTime);
      setTestSaved(true);
      setTimeLeft(0);
      setShowCompletionPopup(true);
      // console.log("Result saved successfully!");
    } catch (error) {
      console.error("Error saving result:", error);
    }
  };

  const handleTimeExpired = () => {
    saveResult();
  };

  const handleFirst = () => {
    setCurrentIndex(0);
  };

  const handleLast = () => {
    setCurrentIndex(questions.length - 1);
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(i => i + 1);
    }
  };

  const isQuestionAnswered = (index) => {
    const answer = answers[index];
    if (Array.isArray(answer)) {
      return answer.length > 0;
    }
    return answer !== undefined && answer !== null && answer !== "";
  };

  const isQuestionCorrect = (index) => {
    const q = questions[index];
    const answer = answers[index];

    if (!answer) return false;

    const questionType = q.question_type || "single_choice";

    if (questionType === "single_choice") {
      const normalizedAnswer = String(answer).trim();
      const correctAnswer = q.correct_answer !== undefined ? q.correct_answer : q.correct_answers;
      const normalizedCorrect = String(correctAnswer).trim();
      return normalizedAnswer === normalizedCorrect;
    } else if (questionType === "multi_select") {
      const normalizedCorrect = normalizeCorrectAnswers(q.correct_answers);
      const normalizedAnswer = Array.isArray(answer) ? answer.map(a => String(a).trim()) : [String(answer).trim()];
      return normalizedAnswer.length === normalizedCorrect.length &&
        normalizedAnswer.every(a => normalizedCorrect.includes(a));
    }
    return false;
  };

  const getQuestionStatusColor = (index) => {
    if (!isQuestionAnswered(index)) return "#e9ecef";
    return isQuestionCorrect(index) ? "#d4edda" : "#f8d7da";
  };

  const getQuestionStatusTextColor = (index) => {
    if (!isQuestionAnswered(index)) return "#6c757d";
    return isQuestionCorrect(index) ? "#28a745" : "#dc3545";
  };

  const answeredCount = Object.keys(answers).filter(key => isQuestionAnswered(parseInt(key))).length;
  const remainingQuestions = questions.length - answeredCount;
  const allQuestionsAnswered = answeredCount === questions.length;

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  return (
    <>
      <style>
        {`
          @media (max-width: 768px) {
            .mobile-main-container {
              flex-direction: column !important;
              gap: 0px !important;  // todo - override padding for mobile - 20
            }
            .mobile-left-column {
              flex: 1 !important;
            }
            .mobile-right-column {
              order: 3 !important;
              flex: 1 !important;
              width: 100% !important;
            }
            .mobile-header-section {
              flex-direction: column !important;
              align-items: flex-start !important;
            }
            .mobile-header-buttons {
              flex-direction: row !important;
              flex-wrap: nowrap !important;
              width: 100% !important;
              gap: 6px !important;
              margin-top: 12px !important;
            }
            .mobile-header-btn {
              padding: 8px !important;
              font-size: 12px !important;
              flex: 1 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              min-height: 36px !important;
              white-space: nowrap !important;
              text-overflow: ellipsis !important;
              overflow: hidden !important;
            }
            .mobile-nav-buttons {
              flex-direction: row !important;
              flex-wrap: nowrap !important;
              width: 100% !important;
              gap: 6px !important;
            }
            .mobile-nav-btn {
              padding: 8px !important;
              font-size: 12px !important;
              flex: 1 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              min-height: 36px !important;
              white-space: nowrap !important;
              text-overflow: ellipsis !important;
              overflow: hidden !important;
            }
            .mobile-test-result-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
      <div style={{ padding: "5px 0", minHeight: "100vh" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 5px" }}>
        <div className="mobile-user-header" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", marginBottom: 20, position: "relative" }}>
          <span style={{ fontSize: "16px" }}>{user.email}</span>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              padding: "8px 12px",
              background: "#F1F5F9",
              color: "#333",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "18px"
            }}
          >
            👤
          </button>
          {showDropdown && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: "8px",
                background: "white",
                border: "1px solid #ccc",
                borderRadius: "8px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                minWidth: "150px",
                zIndex: 10
              }}
            >
              <div
                onClick={() => {
                  setShowLogoutWarning(true);
                  setShowDropdown(false);
                }}
                style={{
                  padding: "10px 16px",
                  cursor: "pointer"
                }}
              >
                Logout
              </div>
            </div>
          )}
        </div>
        {showLogoutWarning && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000
            }}
          >
            <div
              style={{
                background: "white",
                padding: 30,
                borderRadius: 10,
                maxWidth: 400,
                textAlign: "center"
              }}
            >
              <h3>Warning</h3>
              <p>Your test progress will be unsaved if you log out. Are you sure?</p>
              <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "center" }}>
                <button
                  onClick={() => setShowLogoutWarning(false)}
                  style={{
                    padding: "8px 6px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    padding: "8px 6px",
                    background: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer"
                  }}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

        {showExitWarning && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000
            }}
          >
            <div
              style={{
                background: "white",
                padding: 30,
                borderRadius: 10,
                maxWidth: 400,
                textAlign: "center"
              }}
            >
              <h3>Warning</h3>
              <p>All your test data will be lost. Are you sure you want to exit?</p>
              <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "center" }}>
                <button
                  onClick={() => setShowExitWarning(false)}
                  style={{
                    padding: "8px 6px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmExit}
                  style={{
                    padding: "8px 6px",
                    background: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer"
                  }}
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        )}

        {showFinishWarning && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000
            }}
          >
            <div
              style={{
                background: "white",
                padding: 30,
                borderRadius: 10,
                maxWidth: 400,
                textAlign: "center"
              }}
            >
              <h3>Confirm Finish</h3>
              <p>You have {remainingQuestions} unanswered question{remainingQuestions !== 1 ? "s" : ""}. Are you sure you want to finish the test?</p>
              <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "center" }}>
                <button
                  onClick={() => setShowFinishWarning(false)}
                  style={{
                    padding: "8px 6px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmFinish}
                  style={{
                    padding: "8px 6px",
                    background: "#28a745",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer"
                  }}
                >
                  Finish
                </button>
              </div>
            </div>
          </div>
        )}

        {showCompletionPopup && testResult && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000
            }}
          >
            <div
              style={{
                background: "white",
                padding: 40,
                borderRadius: 12,
                maxWidth: 500,
                textAlign: "center",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
              }}
            >
              <h2 style={{ color: "#007bff", marginBottom: 20 }}>Your Test finished</h2>
              <h3 style={{ margin: "0 0 10px 0", color: "#333" }}>{testResult.testTitle}</h3>
              <p style={{ fontSize: "14px", color: "#666", margin: "5px 0" }}>
                📅 {formatDate(testResult.createdAt)}
              </p>
              <p style={{ fontSize: "14px", color: "#666", margin: "5px 0" }}>
                ⏱ Time: {formatTimeTaken(testResult.timeTaken)}
              </p>
              <hr style={{ margin: "20px 0", border: "none", borderTop: "1px solid #e0e0e0" }} />
              <h2 style={{ fontSize: "32px", color: "#007bff", margin: "10px 0" }}>
                {testResult.score} / {testResult.totalQuestions} ({testResult.percentage}%)
              </h2>
              <div style={{ margin: "15px 0", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: 8 }}>
                <p style={{ fontSize: "24px", margin: "0 0 10px 0" }}>{testResult.scoreDescription.graphic}</p>
                <p style={{ fontSize: "18px", fontWeight: "bold", color: testResult.scoreDescription.color, margin: "0 0 5px 0" }}>
                  {testResult.scoreDescription.level}
                </p>
                <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
                  {testResult.scoreDescription.status}
                </p>
              </div>
              <p style={{ fontSize: "14px", color: "#28a745", marginTop: "15px" }}>
                Your score is recorded and will be shown in your history data
              </p>
              <button
                onClick={handleReviewStart}
                style={{
                  marginTop: 20,
                  padding: "8px 6px",
                  background: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px"
                }}
              >
                OK - Review All Questions
              </button>
            </div>
          </div>
        )}

        {isPaused && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1500
            }}
          >
            <div
              style={{
                background: "white",
                padding: 40,
                borderRadius: 10,
                maxWidth: 400,
                textAlign: "center"
              }}
            >
              <h2>Test Paused</h2>
              <p>Click Resume to continue your test.</p>
              <button
                onClick={handlePause}
                style={{
                  marginTop: 20,
                  padding: "8px 6px",
                  background: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "18px"
                }}
              >
                Resume
              </button>
            </div>
          </div>
        )}

        <div className="mobile-main-container" style={{ display: "flex", marginTop: 40, gap: 20 }}>
          <div className="mobile-left-column" style={{ flex: 0.7 }}>
            <div style={{ marginBottom: 20 }}>
                <div className="mobile-header-section" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h2 style={{ margin: 0 }}>{selectedTest.title}</h2>
                <div className="mobile-header-buttons" style={{ display: "flex", gap: 10 }}>
                  {!reviewMode && (
                    <button
                      className="mobile-header-btn"
                      onClick={handlePause}
                      disabled={testSaved}
                      style={{
                        padding: "8px 6px",
                        background: testSaved ? "#6c757d" : "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: testSaved ? "not-allowed" : "pointer",
                        opacity: testSaved ? 0.5 : 1
                      }}
                    >
                      {isPaused ? "▶ Resume" : "⏸ Pause"}
                    </button>
                  )}
                  <button
                    className="mobile-header-btn"
                    onClick={handleExit}
                    disabled={testSaved && !reviewMode}
                    style={{
                      padding: "8px 6px",
                      background: (testSaved && !reviewMode) ? "#6c757d" : "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: (testSaved && !reviewMode) ? "not-allowed" : "pointer",
                      opacity: (testSaved && !reviewMode) ? 0.5 : 1
                    }}
                  >
                    ✕ Exit
                  </button>
                  {!reviewMode && (
                    <button
                      className="mobile-header-btn"
                      onClick={handleFinish}
                      disabled={testSaved}
                      style={{
                        padding: "8px 6px",
                        background: testSaved ? "#6c757d" : allQuestionsAnswered ? "#28a745" : "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: testSaved ? "not-allowed" : "pointer",
                        opacity: testSaved ? 0.5 : 1
                      }}
                    >
                      ▉ Finish
                    </button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: "14px", color: "#666" }}>
                <span>{questions.length} questions</span>
                {selectedTest.difficulty && <span> | Difficulty: {selectedTest.difficulty}</span>}
              </div>
            </div>

            {!reviewMode && selectedTest.isTimed ? (
              <div style={{ textAlign: "right", marginBottom: 20 }}>
                <Timer onTimeExpired={handleTimeExpired} />
              </div>
            ) : reviewMode && testSaved ? (
              <div style={{ marginBottom: 20, padding: "15px", border: "1px solid #e0e0e0", borderRadius: 8, backgroundColor: "#F1F5F9" }}>
                  <div className="mobile-test-result-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <h3 style={{ margin: 0 }}>Test Results</h3>
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <p style={{ margin: 0 }}><strong>Score: {testResult?.score} / {testResult?.totalQuestions} ({testResult?.percentage}%)</strong></p>
                    </div>
                    <div>
                      <p style={{ fontSize: "14px", margin: 0 }}>Level: {testResult?.scoreDescription?.graphic} {testResult?.scoreDescription?.level}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: "14px", margin: 0 }}>⏱ Time: {formatTimeTaken(testResult?.timeTaken)}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: "14px", margin: 0 }}>Status: {testResult?.scoreDescription?.status}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: "14px", margin: 0 }}>📅 {formatDate(testResult?.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ) : null}

            <div style={{ marginBottom: 20 }}>
              <Question reviewMode={reviewMode} testResult={testResult} />
            </div>

            <div className="mobile-nav-buttons" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 30, gap: 6 }}>
              <button
                className="mobile-nav-btn"
                onClick={handleFirst}
                style={{
                  padding: "8px 6px",
                  background: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                {"<<"} First
              </button>
              <button
                className="mobile-nav-btn"
                onClick={handlePrev}
                disabled={currentIndex === 0}
                style={{
                  padding: "8px 6px",
                  background: currentIndex === 0 ? "#6c757d" : "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: currentIndex === 0 ? "not-allowed" : "pointer",
                  opacity: currentIndex === 0 ? 0.5 : 1
                }}
              >
                {"<"} Prev
              </button>
              <button
                className="mobile-nav-btn"
                onClick={handleNext}
                disabled={currentIndex === questions.length - 1}
                style={{
                  padding: "8px 6px",
                  background: currentIndex === questions.length - 1 ? "#6c757d" : "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: currentIndex === questions.length - 1 ? "not-allowed" : "pointer",
                  opacity: currentIndex === questions.length - 1 ? 0.5 : 1
                }}
              >
                Next {">"}
              </button>
              <button
                className="mobile-nav-btn"
                onClick={handleLast}
                style={{
                  padding: "8px 6px",
                  background: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                Last {">>"}
              </button>
            </div>
          </div>

          <div className="mobile-right-column" style={{ flex: 0.3 }}>
            <div style={{ border: "1px solid #ccc", borderRadius: 8, padding: 10, backgroundColor: "#F1F5F9" }}>
              <h4 style={{ margin: 0, marginBottom: 15 }}>Questions</h4>
              {!reviewMode && (
                <div style={{ marginBottom: 15, fontSize: "14px", color: "#666" }}>
                  Remaining: {remainingQuestions}
                </div>
              )}
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {questions.map((q, i) => (
                  <li
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    style={{
                      padding: 8,
                      margin: "4px 0",
                      cursor: "pointer",
                      background: i === currentIndex ? "#007bff" : "#fff",
                      color: i === currentIndex ? "white" : reviewMode ? getQuestionStatusTextColor(i) : isQuestionAnswered(i) ? "#6c757d" : "#333",
                      borderRadius: 4,
                      fontSize: "14px"
                    }}
                  >
                    <strong>Q{i + 1}</strong>
                    <div style={{ fontSize: "12px", marginTop: 4 }}>
                      {truncateText(q.question, 25)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default TestPage;
